import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import { Button } from './ui/Button';
import { PageLoader } from './ui/PageLoader';
import { AdminQueueState } from './ui/AdminQueueState';

type CertStatus = 'unverified' | 'pending_review' | 'certified' | 'rejected' | 'revoked';

type QueueRow = {
  vehicle: {
    id: number;
    display_name: string;
    owner_username?: string | null;
    certification_status: CertStatus;
    modification_count?: number | null;
    base_rarity_stars?: number | null;
    image_url?: string | null;
    public_slug?: string | null;
  };
  request: {
    id: number;
    statement: string;
    evidence: Record<string, unknown>;
    admin_notes?: string | null;
    created_at?: string | null;
    status?: string;
  } | null;
  eligibility?: {
    eligible: boolean;
    reasons: string[];
    checks?: Record<string, unknown>;
  };
};

const STATUSES: CertStatus[] = [
  'pending_review',
  'certified',
  'rejected',
  'revoked',
  'unverified',
];

const AdminVehicleCertifications: React.FC = () => {
  const [status, setStatus] = useState<CertStatus>('pending_review');
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [notesById, setNotesById] = useState<Record<number, string>>({});

  const load = async (s = status) => {
    setLoading(true);
    try {
      const res = await apiService.adminListVehicleCertifications(s);
      setRows((res as { data?: QueueRow[] }).data || []);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when filter changes
  }, [status]);

  const review = async (
    vehicleId: number,
    decision: 'certify' | 'reject' | 'revoke' | 'request_info'
  ) => {
    const notes = (notesById[vehicleId] || '').trim();
    if ((decision === 'reject' || decision === 'request_info') && notes.length < 3) {
      setError('Notes are required to reject or request more info.');
      return;
    }
    setBusyId(vehicleId);
    setError(null);
    try {
      await apiService.adminReviewVehicleCertification(vehicleId, decision, notes);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Review failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-6 text-white">
      <PageHeader eyebrow="Admin" title="Certifications" description="Review Bilt Certified vehicle requests." />
      <p className="text-sm text-gray-400 mb-4 max-w-2xl">
        Human honesty review for public builds: photos and listed mods checked. Not a valuation or
        title search.
      </p>
      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={status === s ? 'filter-chip-active' : 'filter-chip'}
            onClick={() => setStatus(s)}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      {loading ? <PageLoader message="Loading certification queue" /> : null}
      {!loading && error && rows.length === 0 ? (
        <AdminQueueState
          variant="error"
          title="Could not load certification queue"
          description={error}
          onRetry={() => void load()}
        />
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <AdminQueueState
          variant={status === 'pending_review' ? 'empty' : 'filter-empty'}
          title={
            status === 'pending_review'
              ? 'No certification requests in the queue'
              : `No builds with status “${status.replace(/_/g, ' ')}”`
          }
          description={
            status === 'pending_review'
              ? 'Owner-submitted certification requests will appear here.'
              : 'Try another status filter.'
          }
          onClearFilter={status !== 'pending_review' ? () => setStatus('pending_review') : undefined}
          clearFilterLabel="Show pending review"
        />
      ) : null}
      {error && rows.length > 0 ? (
        <p role="alert" className="text-red-300 mb-3 text-sm">
          {error}
        </p>
      ) : null}
      <ul className="space-y-4">
        {rows.map((row) => {
          const v = row.vehicle;
          const r = row.request;
          const evidence = r?.evidence || {};
          const photoIds = Array.isArray(evidence.photo_ids) ? evidence.photo_ids : [];
          return (
            <li
              key={v.id}
              className="rounded-xl border border-white/[0.06] p-4 flex flex-col gap-3"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                {v.image_url ? (
                  <img
                    src={apiService.getFileUrl(v.image_url) || v.image_url}
                    alt=""
                    className="w-28 h-20 object-cover rounded-lg bg-[#0a0a0a] flex-shrink-0"
                  />
                ) : (
                  <div className="w-28 h-20 rounded-lg bg-[#0a0a0a] flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-2">
                    {v.owner_username && v.public_slug ? (
                      <Link
                        to={`/${v.owner_username}/${v.public_slug}`}
                        className="text-orange-400 hover:text-orange-300 font-medium"
                      >
                        {v.display_name}
                      </Link>
                    ) : (
                      <span className="font-medium">{v.display_name}</span>
                    )}
                    <span className="text-xs text-gray-500">{v.certification_status.replace(/_/g, ' ')}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    @{v.owner_username || '-'} · {v.modification_count ?? 0} mods
                    {v.base_rarity_stars != null ? ` · ★${v.base_rarity_stars}` : ''}
                    {r?.created_at ? ` · requested ${r.created_at}` : ''}
                  </p>
                  {r?.statement ? (
                    <p className="text-sm text-gray-300 mt-2 whitespace-pre-wrap">{r.statement}</p>
                  ) : null}
                  {photoIds.length > 0 ? (
                    <p className="text-xs text-gray-500 mt-1">Evidence photo ids: {photoIds.join(', ')}</p>
                  ) : null}
                  {row.eligibility && !row.eligibility.eligible ? (
                    <p className="text-xs text-amber-300/90 mt-1">
                      Eligibility flags: {row.eligibility.reasons.join('; ')}
                    </p>
                  ) : null}
                </div>
              </div>
              <label className="block text-sm text-gray-400">
                Admin notes
                <textarea
                  className="input input-textarea mt-1"
                  value={notesById[v.id] || ''}
                  onChange={(e) => setNotesById((prev) => ({ ...prev, [v.id]: e.target.value }))}
                  placeholder="Required for reject / request info"
                />
              </label>
              <div className="flex flex-wrap gap-2">
                {status === 'pending_review' || status === 'rejected' || status === 'unverified' ? (
                  <Button
                    variant="primary"
                    disabled={busyId === v.id}
                    onClick={() => void review(v.id, 'certify')}
                  >
                    Certify
                  </Button>
                ) : null}
                {status === 'pending_review' ? (
                  <>
                    <Button
                      variant="secondary"
                      disabled={busyId === v.id}
                      onClick={() => void review(v.id, 'request_info')}
                    >
                      Request info
                    </Button>
                    <Button
                      variant="danger"
                      disabled={busyId === v.id}
                      onClick={() => void review(v.id, 'reject')}
                    >
                      Reject
                    </Button>
                  </>
                ) : null}
                {status === 'certified' ? (
                  <Button
                    variant="danger"
                    disabled={busyId === v.id}
                    onClick={() => void review(v.id, 'revoke')}
                  >
                    Revoke
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AdminVehicleCertifications;
